import React, { useState, useEffect } from 'react';
import { BASE_DOMAIN, autoGenDnsPattern, isBaseDomain } from '../utility/baseDomain';
import {Link, useNavigate} from 'react-router-dom';
import { track } from '../utility/analytics';
import '../styles/CreateInstancePage.scss';
import { useSelector, useDispatch } from 'react-redux';
import { FaUbuntu, FaCloud } from 'react-icons/fa';
import { SiRockylinux } from 'react-icons/si';
import { RootState, AppDispatch } from '../store/store';
import Select, {MultiValue, SingleValue} from 'react-select'
import { getSelectStyles } from '../utility/selectStyles';
import { getKeycloakInstance } from '../utility/keycloak';
import {fetchSecurityGroupRules} from "../store/slices/securityRulesSlice";
import axiosInstance from "../utility/axiosInstance";
import {handleAxiosError} from "../helpers/handleAxiosError";
import { useToast } from '../components/ToastContext';
import Modal from '../components/Modal';
import { v4 as uuidv4 } from 'uuid';
import cloudInitTemplates from '../data/cloudInitTemplates.json';
import { fetchDomains } from '../store/slices/domainsSlice';
import { store } from '../store/store';
import { useLimitedTrial } from '../hooks/useLimitedTrial';
import { formatRam } from '../utility/flavorOptions';

interface CloudInitFormField {
    key: string;
    label: string;
    type: string;
    placeholder: string;
    required: boolean;
    validation: string;
}

interface CloudInitFieldOverride {
    label?: string;
    type?: string;
    placeholder?: string;
    required?: boolean;
    validation?: string;
}

interface CloudInitTemplate {
    name: string;
    description: string;
    template: string;
    tags: string[];
    minFlavor?: string;
    allowedImages?: string[];
    fieldOverrides?: Record<string, CloudInitFieldOverride>;
}

interface Image {
    id: string;
    name: string;
    min_ram?: number;
    min_disk?: number;
}

interface Flavor {
    restricted?: boolean;
    restricted_reason?: string;
    id: string;
    name: string;
    vcpus: string;
    ram: string;
    disk: string;
    hourly_rate?: number | null;
}

interface SecurityGroupOption {
    value: string;
    label: string;
}

const CreateInstancePage: React.FC = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const generateShortId = (length = 8) => {
        const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < length; i++) {
            result += chars[Math.floor(Math.random() * chars.length)];
        }
        return result;
    };

    const generateDnsName = () => {
        const base = formData.name ? formData.name.toLowerCase().replace(/[^a-z0-9-]/g, '-') : 'instance';
        return `${base}-${generateShortId(8)}.${BASE_DOMAIN}`;
    };

    const [keypairs, setKeypairs] = useState<any[]>([]);
    const dispatch = useDispatch<AppDispatch>();
    const [error, setError] = useState<string | null>(null);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [dnsEntries, setDnsEntries] = useState<{ dname: string; port: string }[]>([{ dname: '', port: '' }]);

    const [formData, setFormData] = useState({
        name: '',
        image: '',
        imageName: '',
        flavor: '',
        securityGroups: [] as SecurityGroupOption[],
        keypair: null as { value: string; label: string } | null,
        network: null as { value: string; label: string } | null,
        isPublic: true,
        createVolume: false,
        volumeSize: '',
        volumeName: '',
        enableSSH: true,
        userData: '',
    });
    const [showAllImages, setShowAllImages] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [progress, setProgress] = useState<string | null>(null);
    const [showTemplatesModal, setShowTemplatesModal] = useState(false);
    const [showZammadFormModal, setShowZammadFormModal] = useState(false);
    const [selectedFormTemplate, setSelectedFormTemplate] = useState<CloudInitTemplate | null>(null);
    const [templateFormValues, setTemplateFormValues] = useState<Record<string, string>>({});
    const [templateFormErrors, setTemplateFormErrors] = useState<Record<string, string>>({});

    const [estimatedCost, setEstimatedCost] = useState<number | null>(null);
    const [estimatedCostMonth, setEstimatedCostMonth] = useState<number | null>(null);

    const FLAVOR_ORDER = ['tiny', 'small', 'medium', 'large', 'xlarge'];

    const extractTemplateVariables = (template: string): string[] => {
        const regex = /\{\{([A-Z0-9_]+)\}\}/g;
        const vars: string[] = [];
        const seen = new Set<string>();
        let match;
        while ((match = regex.exec(template)) !== null) {
            if (!seen.has(match[1])) {
                seen.add(match[1]);
                vars.push(match[1]);
            }
        }
        return vars;
    };

    const generateLabelFromKey = (key: string): string => {
        return key
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const inferFieldType = (key: string): { type: string; validation: string } => {
        const lower = key.toLowerCase();
        if (lower.includes('email')) return { type: 'email', validation: 'email' };
        if (lower.includes('password')) return { type: 'password', validation: 'password' };
        return { type: 'text', validation: 'notEmpty' };
    };

    const buildFormFields = (template: CloudInitTemplate): CloudInitFormField[] => {
        const vars = extractTemplateVariables(template.template);
        return vars.map(key => {
            const override = template.fieldOverrides?.[key];
            const inferred = inferFieldType(key);
            return {
                key,
                label: override?.label || generateLabelFromKey(key),
                type: override?.type || inferred.type,
                placeholder: override?.placeholder || generateLabelFromKey(key),
                required: override?.required !== undefined ? override.required : true,
                validation: override?.validation || inferred.validation,
            };
        });
    };

    const isImageAllowed = (allowedImages: string[] | undefined): boolean => {
        if (!allowedImages || allowedImages.length === 0) return true;
        const imageType = getSelectedImageType();
        const imageName = (formData.imageName || '').toLowerCase();
        return allowedImages.some(allowed => {
            const lower = allowed.toLowerCase();
            return (imageType && imageType === lower) || imageName.includes(lower);
        });
    };

    const extractFlavorSize = (flavorName: string): string | null => {
        const name = flavorName.toLowerCase();
        if (name.includes('xlarge')) return 'xlarge';
        if (name.includes('large')) return 'large';
        if (name.includes('medium')) return 'medium';
        if (name.includes('small')) return 'small';
        if (name.includes('tiny')) return 'tiny';
        return null;
    };

    const isFlavorSufficient = (minFlavor: string | undefined): boolean => {
        if (!minFlavor) return true;
        const selectedFlavor = flavors.find((f: any) => f.id === formData.flavor);
        // No flavor selected yet - show the template anyway
        if (!selectedFlavor) return true;
        const size = extractFlavorSize(selectedFlavor.name || '');
        // Flavor name doesn't match known sizes - show the template, let user decide
        if (!size) return true;
        const currentLevel = FLAVOR_ORDER.indexOf(size);
        const requiredLevel = FLAVOR_ORDER.indexOf(minFlavor.toLowerCase());
        if (currentLevel === -1 || requiredLevel === -1) return true;
        return currentLevel >= requiredLevel;
    };

    const validateTemplateField = (field: CloudInitFormField, value: string): string => {
        if (field.required && !value.trim()) {
            return `${field.label} is required`;
        }
        switch (field.validation) {
            case 'email': {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value.trim())) return 'Please enter a valid email address';
                break;
            }
            case 'password': {
                if (value.length < 8) return 'Password must be at least 8 characters';
                if (!/[A-Z]/.test(value)) return 'Password must contain an uppercase letter';
                if (!/[a-z]/.test(value)) return 'Password must contain a lowercase letter';
                if (!/[0-9]/.test(value)) return 'Password must contain a number';
                break;
            }
            case 'notEmpty': {
                if (!value.trim()) return `${field.label} cannot be empty`;
                break;
            }
        }
        return '';
    };

    const handleTemplateFormSubmit = () => {
        if (!selectedFormTemplate) return;
        const fields = buildFormFields(selectedFormTemplate);
        const errors: Record<string, string> = {};
        fields.forEach((field) => {
            const err = validateTemplateField(field, templateFormValues[field.key] || '');
            if (err) errors[field.key] = err;
        });
        setTemplateFormErrors(errors);
        if (Object.keys(errors).length > 0) return;

        let result = selectedFormTemplate.template;
        fields.forEach((field) => {
            const placeholder = `{{${field.key}}}`;
            result = result.split(placeholder).join(templateFormValues[field.key] || '');
        });
        setFormData(prev => ({ ...prev, userData: result }));
        setShowZammadFormModal(false);
        setSelectedFormTemplate(null);
        setTemplateFormValues({});
        setTemplateFormErrors({});
    };

    const getSelectedImageType = () => {
        const selectedImage = images.find((img: any) => img.id === formData.image);
        if (!selectedImage) return null;
        const name = (selectedImage.name || '').toLowerCase();
        const metadataBaseImage = ((selectedImage as any).metadata?.base_image_name || '').toLowerCase();
        const baseImage = ((selectedImage as any).base_image_name || '').toLowerCase();

        if (name.includes('ubuntu') || metadataBaseImage.includes('ubuntu') || baseImage.includes('ubuntu')) return 'ubuntu';
        if (name.includes('rocky') || metadataBaseImage.includes('rocky') || baseImage.includes('rocky')) return 'rocky';
        if (name.includes('debian') || metadataBaseImage.includes('debian') || baseImage.includes('debian')) return 'debian';
        if (name.includes('centos') || metadataBaseImage.includes('centos') || baseImage.includes('centos')) return 'centos';
        if (name.includes('fedora') || metadataBaseImage.includes('fedora') || baseImage.includes('fedora')) return 'fedora';
        if (name.includes('cirros') || metadataBaseImage.includes('cirros') || baseImage.includes('cirros')) return 'cirros';

        return null;
    };

    // Estimated cost is derived from the selected flavor's REAL rate (effect below, after flavors load).

    const keycloak = useSelector((state: RootState) => state.auth.token);
    const images = useSelector((state: RootState) => state.images.data);
    const flavors = useSelector((state: RootState) => state.flavors.data);
    const isTrial = useLimitedTrial();

    // Show the SAME price the user is actually billed: the selected flavor's real hourly_rate
    // (from pricing_rules, attached by /v1/flavors). Monthly = hourly x 730. No hardcoded prices.
    useEffect(() => {
        const selected = (flavors as any[]).find((f) => f.id === formData.flavor);
        const rate = selected?.hourly_rate;
        if (formData.flavor && rate != null) {
            setEstimatedCost(rate);
            setEstimatedCostMonth(Number((rate * 730).toFixed(2)));
        } else {
            setEstimatedCost(null);
            setEstimatedCostMonth(null);
        }
    }, [formData.flavor, flavors]);
    const userInfo = useSelector((state: RootState) => state.auth.userInfo);
    const securityGroups = useSelector((state: RootState) => state.securityGroups.data);
    const securityRules = useSelector((state: RootState) => state.securityRules.data);
    const privateNetwork = useSelector((state: RootState) => state.privateNetwork);
    const volumes = useSelector((state: RootState) => state.volumes.data);

    const fetchKeypairs = async () => {
        setLoading(true);

        try {
            const response = await axiosInstance.get('/v1/keypairs');

            if (response.status === 200) {
                setKeypairs(response.data);
            } else {
                throw new Error('Failed to fetch keypairs');
            }
        } catch (err: unknown) {
            const errorMessage = handleAxiosError(err);
            showToast(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchKeypairs();
        fetchSecurityGroupRules();
        dispatch(fetchDomains());
    }, []);

    const isFormValid = () => {
        if (
            formData.name === '' ||
            formData.image === '' ||
            formData.flavor === '' ||
            formData.securityGroups.length === 0 ||
            formData.network === null ||
            formData.keypair === null
        ) {
            return false;
        }

        if (formData.createVolume) {
            if (formData.volumeName === '' || formData.volumeSize === '') {
                return false;
            }
        }

        const safeImages = images || [];
        const safeFlavors = flavors || [];

        const selectedImage = safeImages.find((img) => img.id === formData.image);

        const imageName = selectedImage?.name?.toLowerCase() || '';
        const baseImageName = (selectedImage as any)?.metadata?.base_image_name?.toLowerCase() || (selectedImage as any)?.base_image_name?.toLowerCase() || '';
        const isUbuntuOrRocky = imageName.includes('ubuntu') || baseImageName.includes('ubuntu') || imageName.includes('rocky') || baseImageName.includes('rocky');

        const instanceTypeName = (selectedImage as any)?.metadata?.instance_type_name?.toLowerCase() || '';
        const isFromMediumOrLarger = instanceTypeName.includes('medium') ||
            instanceTypeName.includes('large') ||
            instanceTypeName.includes('xlarge');

        const isRestrictedByMinReq = (selectedImage?.min_ram && Number(formData.flavor) && Number((safeFlavors.find(f => f.id === formData.flavor))?.ram) < selectedImage.min_ram) ||
            (selectedImage?.min_disk && Number(formData.flavor) && Number((safeFlavors.find(f => f.id === formData.flavor))?.disk) < selectedImage.min_disk);

        if (isUbuntuOrRocky || isFromMediumOrLarger || isRestrictedByMinReq) {
            const selectedFlavor = safeFlavors.find((flavor) => flavor.id === formData.flavor);

            if (!selectedFlavor) return false;

            const flavorName = selectedFlavor.name?.toLowerCase() || '';

            const isTinyOrSmall = flavorName.includes('m1.tiny') || flavorName.includes('m1.small');

            if ((isUbuntuOrRocky || isFromMediumOrLarger) && isTinyOrSmall) return false;

            if (selectedImage?.min_ram && Number(selectedFlavor.ram) < selectedImage.min_ram) return false;
            if (selectedImage?.min_disk && Number(selectedFlavor.disk) < selectedImage.min_disk) return false;
        }

        return true;
    };

    const getLaunchGuidance = () => {
        if (!formData.name.trim()) return 'Add an instance name to continue.';
        if (!formData.image) return 'Choose an operating-system image.';
        if (!formData.flavor) return 'Choose an instance size.';
        if (!formData.keypair) return 'Choose an SSH keypair.';
        if (!formData.network) return 'Choose a private network in Advanced options.';
        if (!formData.securityGroups.length) return 'Choose a security group in Advanced options.';
        if (formData.createVolume && (!formData.volumeName.trim() || !formData.volumeSize)) return 'Add a name and size for the new volume.';
        if (!isFormValid()) return 'Choose a size that is compatible with the selected image.';
        return 'Ready to launch.';
    };

    const handleSecurityGroupChange = (
        selected: MultiValue<SecurityGroupOption>
    ) => {
        setFormData({
            ...formData,
            securityGroups: selected as SecurityGroupOption[],
        });
    };

    const securityGroupOptions: SecurityGroupOption[] = securityGroups.map(
        (group: { id: string; name: string }) => ({
            value: group.id,
            label: group.name,
        })
    );

    useEffect(() => {
        if (images.length > 0 && !formData.image) {
            setFormData((prev) => ({ ...prev, image: images[0].id }));
        }
        if (flavors.length > 0 && !formData.flavor) {
            const mediumFlavor = flavors.find((f: any) => f.name.toLowerCase().includes('m1.medium'));
            setFormData((prev) => ({ ...prev, flavor: mediumFlavor ? mediumFlavor.id : flavors[0].id }));
        }
        // if (securityGroups.length > 0 && formData.securityGroups.length === 0) {
        //     setFormData((prev) => ({ ...prev, securityGroups: [
        //             { value: securityGroups[0].id, label: securityGroups[0].name },
        //         ],}));
        // }
        if (securityGroups.length > 0 && formData.securityGroups.length === 0) {
            const nonDefault = securityGroups.find(
                (sg: { name: string }) => sg.name.toLowerCase() !== 'default'
            );

            const selected = nonDefault
                ? { value: nonDefault.id, label: nonDefault.name }
                : { value: securityGroups[0].id, label: securityGroups[0].name }; // fallback to first (possibly "default")

            setFormData((prev) => ({
                ...prev,
                securityGroups: [selected],
            }));
        }
        if (privateNetwork.networks.length > 0 && !formData.network) {
            const firstNet = privateNetwork.networks[0];
            setFormData((prev) => ({
                ...prev,
                network: { value: firstNet.id, label: firstNet.name || 'Unnamed Network' },
            }));
        }
        if (keypairs.length > 0 && !formData.keypair) {
            const kp = keypairs[0]?.keypair?.name;
            if (kp) setFormData((prev) => ({ ...prev, keypair: { value: kp, label: kp } }));
        }

    }, [images, flavors, securityGroups, privateNetwork, keypairs, formData.image, formData.flavor, formData.securityGroups, formData.keypair]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const keypairOptions = keypairs.map((keypair:any ) => ({
        value: keypair.keypair.name,
        label: keypair.keypair.name,
    }));

    const handleKeypairChange = (selected: SingleValue<{ value: string, label: string }>) => {
        setFormData({
            ...formData,
            keypair: selected ? { value: selected.value, label: selected.label } : null,
        });
    };

    const networkOptions = privateNetwork.networks.map(net => ({
        value: net.id,
        label: net.name || 'Unnamed Network',
    }));

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (formData.name === '') {
            showToast("Instance name is required.", 'error');
            return;
        }

        if (formData.name.length > 255) {
            showToast("Instance name is too long (max 255 characters)", 'error');
            return;
        }

        if (!formData.image) {
            showToast("Please select an image.", 'error');
            return;
        }

        if (!formData.flavor) {
            showToast("Please select a flavor.", 'error');
            return;
        }

        if (formData.securityGroups.length === 0) {
            showToast("Please select at least one security group.", 'error');
            return;
        }

        if (!formData.network) {
            showToast("Please select a network.", 'error');
            return;
        }

        if (!formData.keypair) {
            showToast("No keypair is selected.", 'error');
            return;
        }

        if (formData.createVolume) {
            if (formData.volumeName === '') {
                showToast("Volume name is required when creating a volume.", 'error');
                return;
            }
            if (formData.volumeSize === '') {
                showToast("Volume size is required when creating a volume.", 'error');
                return;
            }
        }

        const fqdnPattern = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
        const usedPorts = new Set<number>();
        for (let i = 0; i < dnsEntries.length; i++) {
            const entry = dnsEntries[i];
            const portNum = entry.port ? parseInt(entry.port, 10) : 3000 + i;

            if (usedPorts.has(portNum)) {
                showToast(`DNS entry ${i + 1}: Port ${portNum} is already used in another DNS record for this instance.`, 'error');
                return;
            }
            usedPorts.add(portNum);

            if (entry.dname && isBaseDomain(entry.dname) && !autoGenDnsPattern.test(entry.dname)) {
                showToast(`DNS entry ${i + 1}: name cannot end with .${BASE_DOMAIN}. Leave it empty for a randomly generated one, or click ⟳ Generate.`, 'error');
                return;
            }
            if (entry.dname && !isBaseDomain(entry.dname) && !fqdnPattern.test(entry.dname)) {
                showToast(`DNS entry ${i + 1}: domain name is not a valid FQDN. Only alphanumeric characters and hyphens are allowed, separated by dots.`, 'error');
                return;
            }
            if (entry.dname && !isBaseDomain(entry.dname)) {
                const verified = (store.getState() as any).domains?.domains ?? [];
                const isVerified = verified.some((d: any) => d.domain === entry.dname);
                if (!isVerified) {
                    showToast(`DNS entry ${i + 1}: "${entry.dname}" must be verified first. Go to Account → Domains to verify ownership.`, 'error');
                    return;
                }
            }
            if (entry.port) {
                const portNum = parseInt(entry.port, 10);
                if (isNaN(portNum) || portNum < 1 || portNum > 65535) {
                    showToast(`DNS entry ${i + 1}: port must be a valid TCP port number (1-65535)`, 'error');
                    return;
                }
            }
        }

        if (!isFormValid()) {
            showToast("The selected flavor is not compatible with the selected image. Please check minimum RAM and Disk requirements.", 'error');
            return;
        }

        setLoading(true);

        try {
            await axiosInstance.post(
                '/v1/instances',
                {
                    name: formData.name,
                    image_id: formData.image,
                    imageName: formData.imageName,
                    flavor_id: formData.flavor,
                    security_group_ids: formData.securityGroups.map(sg => sg.value),
                    security_group_names: formData.securityGroups.map(sg => sg.label),
                    keypair_name: formData.keypair?.label,
                    network_id: formData.network?.value,
                    dns_entries: dnsEntries.map((e, idx) => ({ dname: e.dname || undefined, port: e.port || String(3000 + idx) })),
                    user_data: formData.userData || undefined,
                    volume: formData.createVolume
                        ? {
                            name: formData.volumeName,
                            size: Number(formData.volumeSize),
                        }
                        : undefined,
                }
            );

            track('instance-create', { flavor: String(formData.flavor ?? '') });
            navigate('/category/compute/instances');
        } catch (err: unknown) {
            const errorMessage = handleAxiosError(err);
            setError(errorMessage);
            showToast(errorMessage, 'error');
            setShowErrorModal(true);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="fullscreen-center">
                <div className="fancyspinner">
                    <div className="ring"></div>
                    <div className="ringouter"></div>
                    <div className="dot"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="create-instance-page">
            <h1 className="page-title">Launch an instance</h1>
            <a href="https://docs.redu.cloud/Guides/compute-guide#1-launch-an-instance" target="_blank" rel="noopener noreferrer" className="docs-link" style={{ marginLeft: '20px', marginBottom: '20px' }}>
                Documentation →
            </a>
            <div className="content-container">
                <div className="form-container">
                    <form onSubmit={handleFormSubmit}>
                        <div className="form-card">
                            <div className="card-header"><span className="form-card__step">1</span> Name and image</div>
                            <div className="card-body">
                                <div className="instance-name-field">
                                    <label htmlFor="instance-name">Instance name</label>
                                    <input
                                        id="instance-name"
                                        className="input-field"
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="e.g. production-api"
                                        maxLength={255}
                                        required
                                    />
                                </div>
                                <p className="explanation-text">Choose an operating-system image.</p>
                                {(() => {
                                    const isMainOs = (img: any) => {
                                        const n = (img.name || '').toLowerCase();
                                        const base = ((img.metadata?.base_image_name || img.base_image_name) || '').toLowerCase();
                                        const mainOsNames = ['ubuntu', 'rocky', 'debian', 'centos', 'alma', 'fedora', 'windows', 'rhel', 'oracle'];
                                        return mainOsNames.some(os => n.includes(os) || base.includes(os));
                                    };
                                    const primaryImages = images.filter((img: any) => isMainOs(img));
                                    const otherImages   = images.filter((img: any) => !isMainOs(img));
                                    const visibleImages = showAllImages ? images : primaryImages;

                                    const handleImageClick = (image: any) => {
                                        const selectedImage = images.find((img: any) => img.id === image.id);
                                        const isUbuntuOrRocky = ['ubuntu', 'rocky'].some(os =>
                                            (selectedImage?.name?.toLowerCase() || '').includes(os) ||
                                            ((selectedImage as any)?.metadata?.base_image_name?.toLowerCase() || '').includes(os)
                                        );
                                        const instanceTypeName = (selectedImage as any)?.metadata?.instance_type_name?.toLowerCase() || '';
                                        const isFromMediumOrLarger = ['medium', 'large', 'xlarge'].some(s => instanceTypeName.includes(s));

                                        setFormData(prev => {
                                            let newFlavor = prev.flavor;
                                            const currentFlavor = flavors.find((f: any) => f.id === prev.flavor);
                                            const flavorName = currentFlavor?.name?.toLowerCase() || '';
                                            const isTinyOrSmall = flavorName.includes('m1.tiny') || flavorName.includes('m1.small');
                                            const isTooSmall = currentFlavor && (
                                                (selectedImage?.min_ram && Number(currentFlavor.ram) < selectedImage.min_ram) ||
                                                (selectedImage?.min_disk && Number(currentFlavor.disk) < selectedImage.min_disk)
                                            );
                                            if (((isUbuntuOrRocky || isFromMediumOrLarger) && isTinyOrSmall) || isTooSmall) {
                                                const med = flavors.find((f: any) => f.name.toLowerCase().includes('m1.medium'));
                                                if (med) newFlavor = med.id;
                                            }
                                            return { ...prev, image: image.id, imageName: selectedImage?.name || 'Unknown', flavor: newFlavor, userData: '' };
                                        });
                                    };

                                    const getIcon = (img: any) => {
                                        const n = ((img.name || '') + ' ' + (img.metadata?.base_image_name || '') + ' ' + (img.base_image_name || '')).toLowerCase();
                                        if (n.includes('ubuntu')) return <FaUbuntu />;
                                        if (n.includes('rocky')) return <SiRockylinux />;
                                        return <FaCloud />;
                                    };

                                    return (
                                        <>
                                            <div className="boards">
                                                {visibleImages.map((image: any) => (
                                                    <div
                                                        key={image.id}
                                                        className={`image-option ${formData.image === image.id ? 'selected' : ''}`}
                                                        onClick={() => handleImageClick(image)}
                                                    >
                                                        <div className="image-icon">{getIcon(image)}</div>
                                                        <div className="image-name">{image.name}</div>
                                                    </div>
                                                ))}
                                                {!showAllImages && otherImages.length > 0 && (
                                                    <div
                                                        className="image-option image-option--more"
                                                        onClick={() => setShowAllImages(true)}
                                                        title={`Show ${otherImages.length} more image${otherImages.length !== 1 ? 's' : ''} (snapshots & custom)`}
                                                    >
                                                        <div className="image-icon" style={{ fontSize: '1.5rem', fontWeight: 700 }}>+</div>
                                                        <div className="image-name">{otherImages.length} more</div>
                                                    </div>
                                                )}
                                                {showAllImages && (
                                                    <div
                                                        className="image-option image-option--more"
                                                        onClick={() => setShowAllImages(false)}
                                                        title="Show main OS images only"
                                                    >
                                                        <div className="image-icon" style={{ fontSize: '1.2rem' }}>−</div>
                                                        <div className="image-name">less</div>
                                                    </div>
                                                )}
                                            </div>
                                            {!showAllImages && primaryImages.length === 0 && (
                                                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: '8px' }}>
                                                    No standard OS images found. <button type="button" style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0 }} onClick={() => setShowAllImages(true)}>Show all images</button>
                                                </p>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                        
                        <div className="form-card">
                            <div className="card-header"><span className="form-card__step">2</span> Choose a size</div>
                            <div className="card-body">
                                <p className="explanation-text">Compare CPU, memory, storage, and the exact hourly estimate.</p>
                                {isTrial && (
                                    <p className="explanation-text" style={{ color: 'var(--text-muted)' }}>
                                        On the limited trial, sizes above m1.medium need a card. Connect one on the billing page to unlock larger flavors.
                                    </p>
                                )}
                                <div className="table-container tablenomargin">
                                    <div className="table-scroll">
                                        <table className="keypairs-table">
                                            <thead>
                                            <tr>
                                                <th>Select</th>
                                                <th>Flavor Name</th>
                                                <th>vCPUs</th>
                                                <th>RAM</th>
                                                <th>Disk (GB)</th>
                                                <th>Estimate</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(() => {
                                                const selectedImage = images.find(img => img.id === formData.image);
                                                const isUbuntuOrRocky = (selectedImage?.name?.toLowerCase() || '').includes('ubuntu') ||
                                                    ((selectedImage as any)?.metadata?.base_image_name?.toLowerCase() || '').includes('ubuntu') ||
                                                    ((selectedImage as any)?.base_image_name?.toLowerCase() || '').includes('ubuntu') ||
                                                    (selectedImage?.name?.toLowerCase() || '').includes('rocky') ||
                                                    ((selectedImage as any)?.metadata?.base_image_name?.toLowerCase() || '').includes('rocky') ||
                                                    ((selectedImage as any)?.base_image_name?.toLowerCase() || '').includes('rocky');
                                                const instanceTypeName = (selectedImage as any)?.metadata?.instance_type_name?.toLowerCase() || '';
                                                const isFromMediumOrLarger = instanceTypeName.includes('medium') ||
                                                    instanceTypeName.includes('large') ||
                                                    instanceTypeName.includes('xlarge');

                                                // No-card trial caps compute at m1.medium (by RAM + vCPUs vs the real flavor).
                                                const trialCeiling = flavors.find((f: any) => (f.name?.toLowerCase() || '') === 'm1.medium')
                                                    || flavors.find((f: any) => (f.name?.toLowerCase() || '').includes('m1.medium'));

                                                return flavors.map((flavor: Flavor) => {
                                                    const flavorName = flavor.name.toLowerCase();
                                                    const isTinyOrSmall = flavorName.includes('m1.tiny') || flavorName.includes('m1.small');
                                                    const isRestrictedByMinReq = (selectedImage?.min_ram && Number(flavor.ram) < selectedImage.min_ram) ||
                                                                               (selectedImage?.min_disk && Number(flavor.disk) < selectedImage.min_disk);

                                                    const isRestrictedByImage = ((isUbuntuOrRocky || isFromMediumOrLarger) && isTinyOrSmall) || isRestrictedByMinReq;
                                                    const isTrialTooBig = isTrial && (
                                                        (trialCeiling && trialCeiling.ram != null && trialCeiling.vcpus != null)
                                                            ? (Number(flavor.ram) > Number(trialCeiling.ram) || Number(flavor.vcpus) > Number(trialCeiling.vcpus))
                                                            : flavorName.includes('large')
                                                    );
                                                    // The three largest sizes need approval on every account. The API flags them
                                                    // rather than hiding them here (it DOES hide them from agent callers), so the
                                                    // size stays visible and the row says how to ask for it. Hiding it would make a
                                                    // size the pricing page advertises simply vanish, which reads as a bug.
                                                    const needsApproval = !!flavor.restricted;
                                                    const isRestricted = isRestrictedByImage || isTrialTooBig || needsApproval;
                                                    const rate = Number(flavor.hourly_rate);
                                                    const hasRate = Number.isFinite(rate) && rate > 0;
                                                    const restrictTitle = needsApproval
                                                        ? (flavor.restricted_reason || 'Larger sizes need approval. Email office@redu.cloud to request access.')
                                                        : isTrialTooBig
                                                        ? 'Not available on limited trial - larger sizes need your card'
                                                        : (isRestrictedByImage ? 'Not compatible with the selected image' : undefined);

                                                    return (
                                                        <tr
                                                            key={flavor.id}
                                                            title={restrictTitle}
                                                            onClick={() => !isRestricted && setFormData(prev => ({ ...prev, flavor: flavor.id }))}
                                                            style={{
                                                                opacity: isRestricted ? 0.4 : 1,
                                                                cursor: isRestricted ? 'not-allowed' : 'pointer',
                                                                background: formData.flavor === flavor.id ? 'var(--accent-subtle)' : undefined,
                                                                borderLeft: formData.flavor === flavor.id ? '3px solid var(--accent)' : '3px solid transparent',
                                                                transition: 'all 0.15s ease',
                                                            }}
                                                        >
                                                            <td data-label="Select">
                                                                <input
                                                                    type="radio"
                                                                    name="flavor"
                                                                    value={flavor.id}
                                                                    checked={formData.flavor === flavor.id}
                                                                    onChange={handleInputChange}
                                                                    disabled={isRestricted}
                                                                />
                                                            </td>
                                                            <td data-label="Size">{flavor.name}</td>
                                                            <td data-label="vCPU">{flavor.vcpus}</td>
                                                            <td data-label="RAM">{formatRam(flavor.ram)}</td>
                                                            <td data-label="Disk">{flavor.disk} GB</td>
                                                            <td className="flavor-price" data-label="Estimate">
                                                                {hasRate ? <>£{rate.toFixed(rate < 0.01 ? 5 : 4)}/hr <span>· ~£{(rate * 730).toFixed(1)}/mo</span></> : 'Pricing unavailable'}
                                                            </td>
                                                        </tr>
                                                    );
                                                });
                                            })()}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="form-card">
                            <div className="card-header"><span className="form-card__step">3</span> Access</div>
                            <div className="card-body-alt">
                                <p className="explanation-text">Choose the SSH keypair you will use to sign in.</p>

                                <Select
                                    options={keypairOptions}
                                    value={formData.keypair}
                                    onChange={handleKeypairChange}
                                    placeholder="Select Keypair *"
                                    className="security-group-dropdown"
                                    styles={getSelectStyles()}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    required
                                />
                                <div className="instance-access-security">
                                    <label htmlFor="instance-security-groups">Firewall rules</label>
                                    <Select
                                        inputId="instance-security-groups"
                                        isMulti
                                        options={securityGroupOptions}
                                        value={formData.securityGroups}
                                        onChange={handleSecurityGroupChange}
                                        placeholder="Select security groups"
                                        className="security-group-dropdown"
                                        styles={getSelectStyles()}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                            </div>
                        </div>

                        <details className="instance-advanced-options">
                            <summary><span>Advanced options</span><small>Storage, networking, DNS, and startup script</small></summary>
                            <div className="form-card">
                                <div className="card-header">Attach a new volume</div>
                                <div className="card-body-alt">
                                    <label
                                        title={isTrial ? 'Not available on limited trial' : undefined}
                                        style={isTrial ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={formData.createVolume && !isTrial}
                                            disabled={isTrial}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, createVolume: e.target.checked }))}
                                        />
                                        <span className="checkbox-text">
                                            Attach new volume{isTrial ? ' (not available on limited trial)' : ''}
                                        </span>
                                    </label>

                                    {formData.createVolume && (
                                        <div className="space-y-3">
                                            <input
                                                type="text"
                                                className="input-field"
                                                placeholder="Volume name *"
                                                value={formData.volumeName}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, volumeName: e.target.value }))}
                                                required
                                            />
                                            <input
                                                style={{marginTop: "10px"}}
                                                type="number"
                                                className="input-field"
                                                placeholder="Volume size (GB) *"
                                                value={formData.volumeSize}
                                                onChange={(e) => setFormData((prev) => ({ ...prev, volumeSize: e.target.value }))}
                                                min={1}
                                                max={2048}
                                                required
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                        <div className="form-card">
                            <div className="card-header">Select Network</div>
                            <div className="card-body-alt">
                                <p className="explanation-text">Private network for internal traffic.</p>
                                {networkOptions.length > 0 ? (
                                    <Select
                                        options={networkOptions}
                                        value={formData.network}
                                        onChange={(selectedOption) =>
                                            setFormData((prev) => ({
                                                ...prev,
                                                network: selectedOption as { value: string; label: string } | null,
                                            }))
                                        }
                                        placeholder="Select Network"
                                        className="security-group-dropdown"
                                        styles={getSelectStyles()}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                ) : (
                                    <div className="text-sm text-gray-500">
                                        No private networks available. <Link to="/category/network/private">Create one first</Link>.
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="form-card">
                            <div className="card-header">Network Visibility</div>
                            <div className="card-body-alt">
                                <div className="mt-4">
                                    <label className="block mb-1 font-medium">DNS Entries</label>
                                    <p className="text-sm text-gray-500 mt-1" style={{ marginBottom: '10px' }}>
                                        Leave domain blank for a redu.cloud address. Leave port blank to assign one.
                                        {' '}<strong>Custom domains</strong> (not .redu.cloud) must be verified before use -{' '}
                                        <Link to="/category/user/domains" target="_blank" rel="noopener noreferrer">verify a domain</Link>.
                                    </p>
                                    {dnsEntries.map((entry, idx) => (
                                        <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                                            <input
                                                type="text"
                                                placeholder="Domain (e.g. my-app) - optional"
                                                value={entry.dname}
                                                onChange={(e) => {
                                                    const updated = [...dnsEntries];
                                                    updated[idx] = { ...updated[idx], dname: e.target.value };
                                                    setDnsEntries(updated);
                                                }}
                                                className="w-full border border-gray-300 rounded px-3 py-2"
                                                style={{ flex: 2 }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const updated = [...dnsEntries];
                                                    updated[idx] = { ...updated[idx], dname: generateDnsName() };
                                                    setDnsEntries(updated);
                                                }}
                                                title="Generate a random .redu.cloud domain"
                                                className="btn btn-primary instance-primary-button"
                                                style={{ whiteSpace: 'nowrap' }}
                                            >
                                                ⟳ Generate
                                            </button>
                                            <input
                                                type="number"
                                                placeholder="Port (auto)"
                                                value={entry.port}
                                                onChange={(e) => {
                                                    const updated = [...dnsEntries];
                                                    updated[idx] = { ...updated[idx], port: e.target.value };
                                                    setDnsEntries(updated);
                                                }}
                                                min="1"
                                                max="65535"
                                                className="w-full border border-gray-300 rounded px-3 py-2"
                                                style={{ flex: 1, marginTop: '10px' }}
                                            />
                                            {dnsEntries.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setDnsEntries(dnsEntries.filter((_, i) => i !== idx))}
                                                    style={{ background: '#e53e3e', color: 'white', border: 'none', borderRadius: '4px', padding: '6px 10px', cursor: 'pointer', minHeight: '38px' }}
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    {isTrial ? (
                                        <p className="explanation-text" style={{ color: 'var(--text-muted)', marginTop: '4px' }}>
                                            The limited trial allows one DNS per instance. Connect a card to add more.
                                        </p>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => setDnsEntries([...dnsEntries, { dname: '', port: '' }])}
                                            className="btn btn-primary instance-primary-button"
                                            style={{ marginTop: '4px' }}
                                        >
                                            + Add DNS Entry
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="form-card">
                            <div className="card-header">Configuration script (optional)</div>
                            <div className="card-body">
                                <p className="explanation-text">Runs once at first boot.</p>
                                <div style={{ marginBottom: 8, display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <input
                                            id="userDataFile"
                                            type="file"
                                            accept=".yaml,.yml,.txt,.sh"
                                            style={{ display: 'none' }}
                                            onChange={async (e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;
                                                const text = await file.text();
                                                setFormData(prev => ({ ...prev, userData: text }));
                                            }}
                                        />
                                        <label htmlFor="userDataFile" className="btn btn-primary instance-primary-button">Choose File</label>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-primary instance-primary-button"
                                        onClick={() => setShowTemplatesModal(true)}
                                    >
                                        Templates
                                    </button>
                                </div>
                                <textarea
                                    className="input-field"
                                    placeholder="#cloud-config or shell script"
                                    rows={8}
                                    value={formData.userData}
                                    onChange={(e) => setFormData(prev => ({ ...prev, userData: e.target.value }))}
                                />
                            </div>
                        </div>
                        </details>
                    </form>
                </div>

                <aside className="summary-container" aria-labelledby="launch-summary-title">
                    <div className="summary-container__heading">
                        <div>
                            <span>Launch summary</span>
                            <h2 id="launch-summary-title">{formData.flavor ? 'Selected configuration' : 'Choose a size'}</h2>
                        </div>
                        {(estimatedCost !== null || estimatedCostMonth !== null) && (
                            <strong className="summary-container__estimate">
                                £{estimatedCost !== null ? estimatedCost.toFixed(estimatedCost < 0.01 ? 5 : 4) : '…'}/hr
                                <small>~£{estimatedCostMonth !== null ? estimatedCostMonth.toFixed(1) : '…'}/mo</small>
                            </strong>
                        )}
                    </div>
                    <dl className="summary-container__details">
                        {formData.name && <div><dt>Name</dt><dd>{formData.name}</dd></div>}
                        {formData.image && <div><dt>Image</dt><dd>{formData.imageName}</dd></div>}
                        {formData.flavor && <div><dt>Size</dt><dd>{flavors.find((flv) => flv.id === formData.flavor)?.name}</dd></div>}
                        {formData.createVolume && <div><dt>Volume</dt><dd>{formData.volumeName || 'Unnamed'} ({formData.volumeSize || '…'} GB)</dd></div>}
                        {formData.securityGroups.length > 0 && (
                            <div>
                                <dt>Security</dt>
                                <dd>{formData.securityGroups.map((group) => securityGroups.find((sg) => sg.id === group.value)?.name).filter(Boolean).join(', ')}</dd>
                            </div>
                        )}
                    </dl>
                    {progress && <p className="summary-container__progress" aria-live="polite">{progress}</p>}
                    <p id="launch-guidance" className={isFormValid() ? 'summary-container__guidance is-ready' : 'summary-container__guidance'} aria-live="polite">{getLaunchGuidance()}</p>
                    <div className="network-selector">
                        <button
                            type="submit"
                            className="launch-instance-button"
                            onClick={handleFormSubmit}
                            disabled={!isFormValid()}
                            aria-describedby="launch-guidance"
                        >
                            Launch instance
                        </button>
                    </div>
                </aside>
            </div>
            <Modal
                isOpen={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                title="Error Creating Instance"
            >
                <div style={{ padding: '1rem', color: '#c53030' }}>
                    <p>{error}</p>
                    {error?.includes('Payment Required') && (
                        <div style={{ marginTop: '1.5rem' }}>
                            <Link
                                to="/category/user/payment"
                                className="docs-button"
                                style={{ display: 'inline-block', textDecoration: 'none' }}
                                onClick={() => setShowErrorModal(false)}
                            >
                                Resolve Billing Issues
                            </Link>
                        </div>
                    )}
                </div>
            </Modal>
            <Modal
                isOpen={showTemplatesModal}
                onClose={() => setShowTemplatesModal(false)}
                title="Select Cloud-Init Template"
            >
                <div className="templates-modal-content" style={{ padding: '1rem' }}>
                    {(() => {
                        const imageType = getSelectedImageType();
                        const imageName = (formData.imageName || '').toLowerCase();
                        const filteredTemplates = (cloudInitTemplates as CloudInitTemplate[]).filter(t => {
                            const matchesImage = (imageType && t.tags.some(tag => tag.toLowerCase() === imageType)) ||
                                t.tags.some(tag => imageName.includes(tag.toLowerCase()));
                            if (!matchesImage) return false;
                            if (t.minFlavor && !isFlavorSufficient(t.minFlavor)) return false;
                            if (!isImageAllowed(t.allowedImages)) return false;
                            return true;
                        });

                        if (filteredTemplates.length === 0) {
                            return <p>No templates available for this image and flavor combination.</p>;
                        }

                        return (
                            <div style={{ display: 'grid', gap: '1rem' }}>
                                {filteredTemplates.map((template, index) => (
                                    <div
                                        key={index}
                                        className="template-item"
                                        style={{
                                            border: '1px solid var(--border)',
                                            padding: '1rem 1.25rem',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            background: 'var(--surface-elevated)',
                                            transition: 'all 0.15s ease',
                                        }}
                                        onClick={() => {
                                            const variables = extractTemplateVariables(template.template);
                                            if (variables.length > 0) {
                                                setSelectedFormTemplate(template);
                                                setTemplateFormValues({});
                                                setTemplateFormErrors({});
                                                setShowTemplatesModal(false);
                                                setShowZammadFormModal(true);
                                            } else {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    userData: template.template,
                                                }));
                                                // Pre-fill suggested port if template specifies one
                                                if ((template as any).suggestedPort) {
                                                    setDnsEntries(prev => prev.map((e, i) => i === 0 ? { ...e, port: String((template as any).suggestedPort) } : e));
                                                }
                                                setShowTemplatesModal(false);
                                            }
                                        }}
                                        onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; e.currentTarget.style.borderColor = 'var(--accent)'; }}
                                        onMouseOut={(e) => { e.currentTarget.style.background = 'var(--surface-elevated)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                                    >
                                        <h4 style={{ margin: '0 0 0.4rem 0', color: 'var(--text-primary)', fontWeight: 600 }}>{template.name}</h4>
                                        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{template.description}</p>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                </div>
            </Modal>
            <Modal
                isOpen={showZammadFormModal}
                onClose={() => {
                    setShowZammadFormModal(false);
                    setSelectedFormTemplate(null);
                    setTemplateFormValues({});
                    setTemplateFormErrors({});
                }}
                title={`Configure ${selectedFormTemplate?.name || 'Template'}`}
                size="lg"
            >
                <div style={{ padding: '1rem' }}>
                    {selectedFormTemplate && buildFormFields(selectedFormTemplate).map((field) => (
                        <div key={field.key} style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 600 }}>
                                {field.label}{field.required && <span style={{ color: '#c53030' }}> *</span>}
                            </label>
                            <input
                                type={field.type}
                                placeholder={field.placeholder}
                                value={templateFormValues[field.key] || ''}
                                onChange={(e) => {
                                    setTemplateFormValues(prev => ({ ...prev, [field.key]: e.target.value }));
                                    if (templateFormErrors[field.key]) {
                                        setTemplateFormErrors(prev => {
                                            const next = { ...prev };
                                            delete next[field.key];
                                            return next;
                                        });
                                    }
                                }}
                                className="input-field"
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    border: templateFormErrors[field.key] ? '1px solid #c53030' : '1px solid #ddd',
                                    borderRadius: '4px'
                                }}
                            />
                            {templateFormErrors[field.key] && (
                                <p style={{ color: '#c53030', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                                    {templateFormErrors[field.key]}
                                </p>
                            )}
                        </div>
                    ))}
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                        <button
                            type="button"
                            className="btn btn-primary"
                            style={{ color: 'white' }}
                            onClick={handleTemplateFormSubmit}
                        >
                            Apply Template
                        </button>
                        <button
                            type="button"
                            className="btn"
                            style={{ background: '#e2e8f0' }}
                            onClick={() => {
                                setShowZammadFormModal(false);
                                setSelectedFormTemplate(null);
                                setTemplateFormValues({});
                                setTemplateFormErrors({});
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default CreateInstancePage;
